import { API_CONFIG, APP_CONFIG } from '@/lib/config/api.config';
import { AuthenticationError } from '@/lib/errors/auth.errors';
import { adminService } from '@/lib/services';
import { apiService } from '@/lib/services/api.service';
import {
  CognitoError,
  CognitoService,
  CognitoSignInResult,
} from '@/lib/services/cognito.service';
import { sidebarActions } from '@/lib/store/sidebar.store';
import { logger } from '@/lib/utils/logger';
import type {
  AuthCredentials,
  AuthResponse,
  LoginResult,
  RegisterCredentials,
} from '@/types';
import { Session } from 'next-auth';
import { signOut } from 'next-auth/react';

class AuthService {
  private cognitoService: CognitoService;

  constructor() {
    this.cognitoService = new CognitoService();
  }

  private async validateSession(token: string): Promise<boolean> {
    try {
      const decodedToken = JSON.parse(
        Buffer.from(token.split('.')[1], 'base64').toString()
      );

      // Check token expiration
      if (decodedToken.exp && Date.now() >= decodedToken.exp * 1000) {
        logger.warn('Session expired', { userId: decodedToken.sub });
        return false;
      }

      // Validate with backend
      const response = await apiService.post<{ valid: boolean }>(
        '/auth/validate-session',
        {
          token,
          userId: decodedToken.sub,
        }
      );

      return response?.valid || false;
    } catch (error) {
      logger.error('Session validation failed', { error });
      return false;
    }
  }

  async login(credentials: AuthCredentials): Promise<LoginResult> {
    logger.info('Attempting login', { email: credentials.email });

    try {
      // First authenticate with Cognito
      const cognitoResult = await this.cognitoService.signIn(
        credentials.email,
        credentials.password
      );

      logger.info('Cognito authentication successful', {
        email: credentials.email,
        status: cognitoResult.status,
      });

      // Decode both access and ID tokens
      const accessTokenData = cognitoResult.accessToken
        ? JSON.parse(
            Buffer.from(
              cognitoResult.accessToken.split('.')[1],
              'base64'
            ).toString()
          )
        : null;

      const idTokenData = cognitoResult.idToken
        ? JSON.parse(
            Buffer.from(
              cognitoResult.idToken.split('.')[1],
              'base64'
            ).toString()
          )
        : null;

      if (!accessTokenData || !idTokenData) {
        logger.error('Invalid Cognito tokens', { email: credentials.email });
        throw AuthenticationError.invalidCredentials();
      }

      // If user is not confirmed, return early
      if (cognitoResult.status === 'UNCONFIRMED') {
        logger.info('User not confirmed', { email: credentials.email });
        return {
          user: {
            id: idTokenData.sub,
            email: idTokenData.email,
            name: idTokenData.name || null,
            hasFilledQuestionnaire: false,
            formAnswerId: '',
            language: 'fr',
            isAdmin: false,
            role: '',
          },
          tokens: {
            accessToken: cognitoResult.accessToken,
            idToken: cognitoResult.idToken,
          },
          status: 'UNCONFIRMED',
        };
      }

      // Then authenticate with our backend using server-safe method
      try {
        const apiUrl = API_CONFIG.BASE_URL + API_CONFIG.ENDPOINTS.AUTH.LOGIN;

        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: credentials.email,
            cognitoToken: cognitoResult.accessToken,
            idToken: cognitoResult.idToken,
            cognitoId: idTokenData.sub,
            cognitoAttributes: {
              name: idTokenData.name,
              email: idTokenData.email,
              email_verified: idTokenData.email_verified,
              picture: idTokenData.picture,
              phone_number: idTokenData.phone_number,
            },
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const backendResponse = await response.json();

        if (!backendResponse?.user) {
          logger.error('Invalid backend response', {
            response: backendResponse,
          });
          // Instead of throwing an error, create a minimal user response
          return {
            user: {
              id: backendResponse?.user?.id || '',
              email: idTokenData.email,
              name: idTokenData.name || null,
              hasFilledQuestionnaire: false,
              formAnswerId: '',
              language: 'fr',
              isAdmin: false,
              role: '',
            },
            tokens: {
              accessToken: cognitoResult.accessToken,
              idToken: cognitoResult.idToken,
            },
            status: 'CONFIRMED',
          };
        }

        // Fetch additional user data
        const additionalData = await this.getUserProfileServer(
          backendResponse.user.id,
          cognitoResult.accessToken
        );

        return {
          user: {
            id: backendResponse.user.id,
            email: backendResponse.user.email,
            name: backendResponse.user.name || null,
            hasFilledQuestionnaire:
              additionalData.hasFilledQuestionnaire || false,
            formAnswerId: additionalData.formAnswerId || '',
            language: additionalData.language || 'fr',
            status: 'CONFIRMED',
            city: additionalData.city || '',
            ageGroup: additionalData.ageGroup || '',
            acceptedPolicies: additionalData.acceptedPolicies || false,
            picture: additionalData.picture || '',
            emailVerified: true,
            isAdmin: additionalData.isAdmin || false,
            isSubscribed: additionalData.isSubscribed || false,
            createdAt: additionalData.createdAt || '',
            updatedAt: additionalData.updatedAt || '',
            role: additionalData.role || '',
          },
          tokens: {
            accessToken: cognitoResult.accessToken,
            idToken: cognitoResult.idToken,
          },
          status: 'CONFIRMED',
        };
      } catch (backendError) {
        logger.warn('Backend login failed, using Cognito data', {
          error: backendError,
          email: credentials.email,
        });

        throw backendError;
      }
    } catch (error) {
      logger.error('Login failed', {
        error,
        email: credentials.email,
      });

      if (error instanceof AuthenticationError) {
        throw error;
      }

      throw AuthenticationError.invalidCredentials();
    }
  }

  async refreshToken(currentToken: string): Promise<LoginResult> {
    logger.info('Attempting token refresh');

    try {
      // Validate current session first
      const isValid = await this.validateSession(currentToken);
      if (!isValid) {
        logger.warn('Invalid session during token refresh');
        throw AuthenticationError.sessionExpired();
      }

      const decodedToken = JSON.parse(
        Buffer.from(currentToken.split('.')[1], 'base64').toString()
      );
      const cognitoResult = await this.cognitoService.refreshToken(
        decodedToken.sub
      );

      if (!cognitoResult) {
        logger.error('Token refresh failed - no result');
        throw AuthenticationError.tokenInvalid();
      }

      logger.info('Token refresh successful', { userId: cognitoResult.userId });

      // Then authenticate with our backend, passing Cognito tokens
      try {
        const backendResponse = await apiService.post<AuthResponse>(
          API_CONFIG.ENDPOINTS.AUTH.LOGIN,
          {
            email: cognitoResult.email,
            cognitoToken: cognitoResult.accessToken,
            cognitoId: cognitoResult.userId,
            cognitoAttributes: {
              name: cognitoResult.name,
              email: cognitoResult.email,
            },
          }
        );

        if (!backendResponse?.user) {
          logger.error('Invalid backend response', {
            response: backendResponse,
          });
          // Instead of throwing an error, create a minimal user response
          return {
            user: {
              id: cognitoResult.userId,
              email: cognitoResult.email,
              name: cognitoResult.name,
              hasFilledQuestionnaire: false,
              formAnswerId: '',
              language: 'fr',
              role: '',
            },
            tokens: {
              accessToken: cognitoResult.accessToken,
              idToken: cognitoResult.idToken,
            },
            status: 'CONFIRMED',
          };
        }

        logger.info('Login successful', {
          userId: backendResponse.user.id,
          email: cognitoResult.email,
        });

        return {
          user: {
            id: backendResponse.user.id,
            email: backendResponse.user.email,
            name: backendResponse.user.name || null,
            hasFilledQuestionnaire:
              backendResponse.user.hasFilledQuestionnaire || false,
            formAnswerId: backendResponse.user.formAnswerId || '',
            language: backendResponse.user.language || 'fr',
            role: backendResponse.user.role || '',
          },
          tokens: {
            accessToken: cognitoResult.accessToken,
            idToken: cognitoResult.idToken,
          },
          status: 'CONFIRMED',
        };
      } catch (backendError) {
        logger.warn('Backend login failed, using Cognito data', {
          error: backendError,
          email: cognitoResult.email,
        });

        // If backend fails, still return a valid user using Cognito data
        return {
          user: {
            id: cognitoResult.userId,
            email: cognitoResult.email,
            name: cognitoResult.name,
            hasFilledQuestionnaire: false,
            formAnswerId: '',
            language: 'fr',
            role: '',
          },
          tokens: {
            accessToken: cognitoResult.accessToken,
            idToken: cognitoResult.idToken,
          },
          status: 'CONFIRMED',
        };
      }
    } catch (error) {
      logger.error('Token refresh failed', { error });
      if (error instanceof AuthenticationError) {
        throw error;
      }
      throw AuthenticationError.sessionExpired();
    }
  }

  async register(data: RegisterCredentials): Promise<{
    needsConfirmation: boolean;
    isResend?: boolean;
    message?: string;
    userExists?: boolean;
  }> {
    logger.info('Attempting registration', { email: data.email });

    try {
      // First register with Cognito
      await this.cognitoService.signUp(data.email, data.password, {
        name: data.name,
      });

      logger.info('Cognito registration successful', { email: data.email });

      // Store registration data for use after confirmation
      sessionStorage.setItem(
        'registrationData',
        JSON.stringify({
          email: data.email,
          name: data.name,
          city: data.city,
          ageGroup: data.ageGroup,
          acceptedPolicies: data.acceptedPolicies,
          language: data.language,
          userType: data.userType,
          avatar: data.avatar || '',
          role: data.role,
        })
      );

      // Store password temporarily for post-confirmation sign in
      sessionStorage.setItem('tempPassword', data.password);

      return {
        needsConfirmation: true,
        message: 'Please check your email for a confirmation code.',
      };
    } catch (error) {
      logger.error('Registration failed', {
        error,
        email: data.email,
      });

      // Handle case where user exists but might be unconfirmed
      if (
        error instanceof CognitoError &&
        error.code === 'UsernameExistsException'
      ) {
        try {
          // Try to sign in to check user status
          await this.cognitoService.signIn(data.email, data.password);

          // If sign in succeeds, user exists and is confirmed
          logger.info('User exists and is confirmed', { email: data.email });
          return {
            needsConfirmation: false,
            userExists: true,
            message:
              'This email is already registered. Please sign in instead.',
          };
        } catch (signInError) {
          // If error is UserNotConfirmedException, user exists but is unconfirmed
          if (
            signInError instanceof CognitoError &&
            signInError.code === 'UserNotConfirmedException'
          ) {
            logger.info('User exists but is unconfirmed, resending code', {
              email: data.email,
            });

            // Resend confirmation code
            await this.cognitoService.resendConfirmationCode(data.email);

            // Store registration data for after confirmation
            sessionStorage.setItem(
              'registrationData',
              JSON.stringify({
                email: data.email,
                name: data.name,
                city: data.city,
                ageGroup: data.ageGroup,
                acceptedPolicies: data.acceptedPolicies,
                language: data.language,
                userType: data.userType,
                avatar: data.avatar || '',
                role: data.role,
              })
            );

            // Store password temporarily for post-confirmation sign in
            sessionStorage.setItem('tempPassword', data.password);

            // Return with confirmation needed
            return {
              needsConfirmation: true,
              isResend: true,
              message: 'A new confirmation code has been sent to your email.',
            };
          }

          // If error is NotAuthorizedException (wrong password), user exists and is confirmed
          if (
            signInError instanceof CognitoError &&
            signInError.code === 'NotAuthorizedException'
          ) {
            logger.info('User exists with different password', {
              email: data.email,
            });
            return {
              needsConfirmation: false,
              userExists: true,
              message:
                'This email is already registered. Please sign in instead.',
            };
          }

          // For any other error during sign in, throw registration failed
          logger.error('Unexpected error during registration', {
            email: data.email,
            error: signInError,
          });
          throw AuthenticationError.registrationFailed();
        }
      }

      // For any other registration error, throw appropriate error
      if (error instanceof AuthenticationError) {
        throw error;
      }
      throw AuthenticationError.registrationFailed();
    }
  }

  async confirmSignUp(email: string, code: string): Promise<void> {
    logger.info('Attempting signup confirmation', { email });

    try {
      // First confirm the signup
      await this.cognitoService.confirmSignUp(email, code);
      logger.info('Signup confirmation successful', { email });

      // After successful confirmation, get registration data
      const registrationDataStr = sessionStorage.getItem('registrationData');
      if (registrationDataStr) {
        try {
          const registrationData = JSON.parse(registrationDataStr);

          // Get the stored password for sign in
          const passwordStr = sessionStorage.getItem('tempPassword');
          if (!passwordStr) {
            throw new Error('Password not found');
          }

          // Sign in to get the Cognito ID
          const cognitoResult = await this.cognitoService.signIn(
            email,
            passwordStr
          );
          logger.info('Post-confirmation sign in successful', { email });

          // Register user in our backend with Cognito ID
          await apiService.post(
            API_CONFIG.ENDPOINTS.USERS.REGISTER,
            {
              ...registrationData,
              id: cognitoResult.userId, // Add Cognito ID
            },
            null,
            true
          );
          logger.info('Backend registration successful', { email });
        } catch (backendError) {
          logger.error('Backend registration failed after confirmation', {
            error: backendError,
            email,
          });
          throw AuthenticationError.registrationFailed();
        } finally {
          // Clean up stored data
          sessionStorage.removeItem('registrationData');
          sessionStorage.removeItem('tempPassword');
        }
      }
    } catch (error) {
      logger.error('Signup confirmation failed', {
        error,
        email,
      });

      if (error instanceof AuthenticationError) {
        throw error;
      }
      throw AuthenticationError.confirmationFailed();
    }
  }

  async resendConfirmationCode(email: string): Promise<void> {
    logger.info('Attempting to resend confirmation code', { email });

    try {
      await this.cognitoService.resendConfirmationCode(email);
      logger.info('Confirmation code resent successfully', { email });
    } catch (error) {
      logger.error('Failed to resend confirmation code', {
        error,
        email,
      });

      if (error instanceof AuthenticationError) {
        throw error;
      }
      throw AuthenticationError.confirmationFailed();
    }
  }

  async logout(session: Session | null): Promise<void> {
    logger.info('Logging out', { session: !!session });

    try {
      // Clear admin cache first
      await adminService.clearCache();

      // Clear user preferences from localStorage
      if (typeof window !== 'undefined') {
        try {
          // Clear sidebar preference
          localStorage.removeItem('jaqln_sidebar_open_state');
          // Clear submission confirmation dismissal state
          localStorage.removeItem('jaqln_submission_confirmation_dismissed');

          // Import sidebarActions to clear preferences properly
          sidebarActions.clearSidebarPreference();

          logger.info('User preferences cleared from localStorage');
        } catch (error) {
          logger.error('Failed to clear user preferences from localStorage', {
            error,
          });
        }
      }

      if (session?.user?.accessToken) {
        // Try Cognito logout first while token is still valid
        try {
          await this.cognitoService.signOut(session.user.accessToken);
          logger.info('Cognito logout successful');
        } catch (error: any) {
          // Ignore token revocation errors as they're expected in some cases
          if (error?.name !== 'NotAuthorizedException') {
            logger.error('Cognito logout failed', { error });
          }
        }

        // Then attempt backend logout
        try {
          await apiService.post('/auth/logout', {}, session);
          logger.info('Backend logout successful');
        } catch (error) {
          logger.error('Backend logout failed', { error });
        }
      }
    } catch (error) {
      logger.error('Logout failed completely', { error });
    }
  }

  async handleGoogleSignIn({
    token,
    user,
  }: {
    token: string;
    user: { email: string; name: string; image: string };
  }): Promise<LoginResult> {
    logger.info('Attempting Google sign in', { email: user.email });

    try {
      // Generate a secure password for Cognito (we won't need this for actual auth)
      const securePassword = `${Buffer.from(token).toString('base64').slice(0, 16)}#Aa1`;

      try {
        // Try to sign in first
        const cognitoResult = await this.cognitoService.signIn(
          user.email,
          securePassword
        );

        // If successful, proceed with backend auth
        return this.handleCognitoAuthResult(cognitoResult, user);
      } catch (error: any) {
        // If user doesn't exist, create them
        if (error.code === 'UserNotFoundException') {
          logger.info('Creating new Google user', { email: user.email });

          // Sign up the user in Cognito
          await this.cognitoService.signUp(user.email, securePassword, {
            name: user.name,
            picture: user.image,
            email_verified: 'true',
          });

          // Auto confirm the user since they're coming from Google
          await this.cognitoService.confirmSignUp(user.email, 'AUTO_CONFIRM');

          // Now sign in with the created user
          const cognitoResult = await this.cognitoService.signIn(
            user.email,
            securePassword
          );

          // Proceed with backend auth
          return this.handleCognitoAuthResult(cognitoResult, user);
        }

        throw error;
      }
    } catch (error) {
      logger.error('Google login failed', { error, email: user.email });
      throw AuthenticationError.loginFailed();
    }
  }

  // Server-safe method to fetch user profile
  async getUserProfileServer(
    userId: string,
    accessToken: string
  ): Promise<any> {
    try {
      const apiUrl =
        process.env.NEXT_PUBLIC_API_URL || process.env.NEXT_PUBLIC_NEXT_API_URL;
      if (!apiUrl) {
        throw new Error('API URL not configured');
      }

      const response = await fetch(
        `${apiUrl}${API_CONFIG.ENDPOINTS.USERS.BASE}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        logger.error('Failed to fetch user profile', {
          status: response.status,
          statusText: response.statusText,
          userId,
        });
        return {
          isAdmin: false,
          hasFilledQuestionnaire: false,
          formAnswerId: '',
          language: 'fr',
        };
      }

      const data = await response.json();
      return data;
    } catch (error) {
      logger.error('Error fetching user profile', {
        error: error instanceof Error ? error.message : error,
        userId,
      });
      return {
        isAdmin: false,
        hasFilledQuestionnaire: false,
        formAnswerId: '',
        language: 'fr',
      };
    }
  }

  private async handleCognitoAuthResult(
    cognitoResult: CognitoSignInResult,
    user: { email: string; name: string; image: string }
  ): Promise<LoginResult> {
    try {
      const backendResponse = await apiService.post<AuthResponse>(
        API_CONFIG.ENDPOINTS.AUTH.LOGIN,
        {
          email: user.email,
          cognitoToken: cognitoResult.accessToken,
          idToken: cognitoResult.idToken,
          cognitoId: cognitoResult.userId,
          cognitoAttributes: {
            name: user.name,
            email: user.email,
            email_verified: 'true',
            picture: user.image,
          },
        },
        null,
        true
      );

      if (!backendResponse?.user) {
        throw new Error('Invalid backend response');
      }

      return {
        user: {
          id: backendResponse.user.id,
          email: backendResponse.user.email,
          name: backendResponse.user.name,
          hasFilledQuestionnaire:
            backendResponse.user.hasFilledQuestionnaire || false,
          formAnswerId: backendResponse.user.formAnswerId || '',
          language: backendResponse.user.language || 'fr',
          status: 'CONFIRMED',
          city: backendResponse.user.city,
          ageGroup: backendResponse.user.ageGroup,
          acceptedPolicies: backendResponse.user.acceptedPolicies,
          picture: backendResponse.user.picture || user.image,
          emailVerified: true,
          isAdmin: backendResponse.user.isAdmin || false,
          isSubscribed: backendResponse.user.isSubscribed || false,
          createdAt: backendResponse.user.createdAt,
          updatedAt: backendResponse.user.updatedAt,
          role: backendResponse.user.role || '',
        },
        tokens: {
          accessToken: cognitoResult.accessToken,
          idToken: cognitoResult.idToken,
        },
        status: 'CONFIRMED',
      };
    } catch (backendError) {
      // If backend fails, create a minimal user response with Cognito data
      return {
        user: {
          id: cognitoResult.userId,
          email: user.email,
          name: user.name,
          hasFilledQuestionnaire: false,
          formAnswerId: '',
          language: 'fr',
          status: 'CONFIRMED',
          picture: user.image,
          emailVerified: true,
          role: '',
        },
        tokens: {
          accessToken: cognitoResult.accessToken,
          idToken: cognitoResult.idToken,
        },
        status: 'CONFIRMED',
      };
    }
  }
}

export const authService = new AuthService();
